import { Link } from '@tanstack/react-router'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { MessagesSquare } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatEventTime } from '@/lib/utils'
import { ConversationListItemContent, ConversationSearch } from '@/features/communications/conversation-list'
import { conversationListItemClass } from '@/features/communications/message-format'
import { ChatConversation } from '@/features/storefront-chat/chat-conversation'
import { useStorefrontChats } from '@/features/storefront-chat/hooks'
import { ChatAlertsControl } from '@/features/storefront-chat/phone-alerts'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { useVisualViewportHeight } from '@/hooks/use-visual-viewport-height'
import { plainPreview, shopperDisplayName } from '@/features/storefront-chat/format'
import type { ChatListItem, ChatView } from '@/features/storefront-chat/types'

const VIEWS: { value: ChatView; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
]

const AUTHOR_PREFIX: Record<string, string> = { ai: 'AI: ', staff: 'Staff: ', shopper: '' }

function ChatListRow({ chat, selected }: { chat: ChatListItem; selected: boolean }) {
  const last = chat.last_message
  return (
    <li>
      <Link
        to="/customer-comms/chats/$conversationId"
        params={{ conversationId: chat.conversation_id }}
        search={true}
        aria-current={selected ? 'true' : undefined}
        data-testid={`chat-list-item-${chat.conversation_id}`}
        className={conversationListItemClass(selected)}
      >
        <ConversationListItemContent
          selected={selected}
          title={
            <span className="flex min-w-0 items-center gap-1.5">
              {chat.awaiting_reply && (
                <span className="h-2 w-2 shrink-0 rounded-full bg-destructive">
                  <span className="sr-only">Needs a reply:</span>
                </span>
              )}
              <span className="truncate">{shopperDisplayName(chat)}</span>
            </span>
          }
          aside={
            chat.unread_count > 0 ? (
              <span
                className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-usa-blue px-1.5 text-[11px] font-semibold tabular-nums text-white"
                aria-label={`${chat.unread_count} unanswered`}
              >
                {chat.unread_count}
              </span>
            ) : (
              <time dateTime={chat.last_activity_at} className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                {formatEventTime(chat.last_activity_at)}
              </time>
            )
          }
          meta={
            last ? (
              <span className="min-w-0 truncate">
                {AUTHOR_PREFIX[last.author] ?? ''}
                {plainPreview(last.body)}
              </span>
            ) : null
          }
        />
      </Link>
    </li>
  )
}

function ViewFilter({ value, onChange }: { value: ChatView; onChange: (view: ChatView) => void }) {
  return (
    <div role="radiogroup" aria-label="Show" className="flex flex-wrap gap-1 rounded-lg bg-muted p-[3px]">
      {VIEWS.map((view) => {
        const active = view.value === value
        return (
          <button
            key={view.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(view.value)}
            className={cn(
              'inline-flex h-7 items-center rounded-md px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {view.label}
          </button>
        )
      })}
    </div>
  )
}

const EMPTY: Record<ChatView, string> = {
  open: 'No open chats. New ones appear here as soon as a shopper writes.',
  closed: 'No closed chats.',
}

export function ChatsPage({ conversationId }: { conversationId?: string }) {
  const [view, setView] = useQueryState(
    'view',
    parseAsStringLiteral(VIEWS.map((option) => option.value)).withDefault('open'),
  )
  const [search, setSearch] = useUrlSearchText()
  const query = search.trim()
  const { data, isLoading } = useStorefrontChats(view, query)
  const chats = data?.items ?? []
  const phone = useMediaQuery(PHONE_QUERY)
  // An open chat on a phone is the whole screen (the dashboard drops its top bar) and fills what
  // the keyboard leaves, so the reply box stays above it.
  const fullScreen = phone && !!conversationId
  const visibleHeight = useVisualViewportHeight(fullScreen)

  return (
    <div
      className={cn(
        'mx-auto flex max-w-[1600px] flex-col gap-3',
        fullScreen
          ? 'h-full'
          : cn('md:h-[calc(100dvh-6.5rem)]', conversationId && 'max-md:-m-4 max-md:h-[calc(100dvh-3.5rem)]'),
      )}
      style={visibleHeight ? { height: visibleHeight } : undefined}
    >
      <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-2', conversationId && 'max-lg:hidden')}>
        <h1 className="mr-auto text-2xl font-bold tracking-tight">Chats</h1>
        <ChatAlertsControl />
        <ViewFilter value={view} onChange={setView} />
        <ConversationSearch
          value={search}
          onChange={setSearch}
          placeholder="Search chats"
          className="w-full sm:w-64"
        />
      </div>

      <div
        className={cn(
          'min-h-0 flex-1 overflow-hidden bg-card lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] xl:grid-cols-[19rem_minmax(0,1fr)]',
          !fullScreen && 'rounded-lg border',
        )}
      >
        <nav
          aria-label="Chats"
          className={cn(
            'min-h-0 overflow-y-auto bg-muted/40 p-1.5 lg:border-r',
            conversationId && 'max-lg:hidden',
          )}
        >
          {isLoading ? (
            <div className="space-y-2 p-1">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : chats.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              {query ? `No chats match “${query}”` : EMPTY[view]}
            </p>
          ) : (
            <ul className="space-y-1">
              {chats.map((chat) => (
                <ChatListRow key={chat.conversation_id} chat={chat} selected={chat.conversation_id === conversationId} />
              ))}
            </ul>
          )}
        </nav>
        <section
          aria-label="Conversation"
          className={cn('h-full min-h-0 min-w-0', !conversationId && 'max-lg:hidden')}
        >
          {conversationId ? (
            <ChatConversation key={conversationId} conversationId={conversationId} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
              <MessagesSquare aria-hidden className="h-6 w-6" />
              <p className="text-sm">Pick a chat to read it and reply.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
